import { useCallback, useState } from 'react'

import { faUserCircle } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, Container, TextField, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import Avatar from '@scoir/avatar'
import { imageAsFullImageSrc } from '@scoir/avatar-image-url'
import { ConfirmationDialog } from '@scoir/modals'

import api from 'common/api'
import telephone from 'common/telephone'
import useSnackbar from 'common/use-snackbar'
import { useAppDispatch } from 'store'
import { User, actions as userActions } from 'store/user'
import { useUser } from 'store/user/hooks'

import { PickerFileMetadata, useImageUpload } from './use-image-upload'

const useStyles = makeStyles((theme) => ({
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  avatarWrapper: {
    marginBottom: theme.spacing(3),
    display: 'flex',
    gridGap: theme.spacing(2),
    alignItems: 'center',
  },
  avatar: {
    height: 160,
    width: 160,
  },
  editAvatarButton: {
    height: 36,
  },
  infoField: {
    padding: theme.spacing(3, 0),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}))

const validatePhoneNum = (fmtNum: string) => {
  if (!fmtNum || fmtNum === '') return true
  return fmtNum.length >= 10 && fmtNum.length <= 13
}

const ProfileContent = () => {
  const classes = useStyles()
  const user = useUser()
  const [isEditing, setIsEditing] = useState(false)
  const handleEditingToggle = () => {
    setIsEditing(!isEditing)
  }

  if (!user) {
    return null
  }

  return (
    <Container maxWidth="lg">
      <div className={classes.sectionHeader}>
        <div>
          <Typography
            variant="h6"
            component="h1"
          >
            Profile
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
          >
            Visible to Counselors at the High Schools you connect with.
          </Typography>
        </div>
        <div>
          <Button
            variant="text"
            color="primary"
            onClick={handleEditingToggle}
          >
            {isEditing ? 'Cancel' : 'Edit'}
          </Button>
        </div>
      </div>
      {isEditing ? (
        <EditProfile
          user={user}
          setIsEditing={setIsEditing}
        />
      ) : (
        <ViewProfile user={user} />
      )}
    </Container>
  )
}

const ViewProfile = ({ user }: { user: User }) => {
  const classes = useStyles()
  return (
    <>
      <div className={classes.avatarWrapper}>
        <Avatar
          aria-label="user icon"
          variant="circular"
          sourceUrl={imageAsFullImageSrc(user.AvatarUrl)}
          className={classes.avatar}
          fallbackIcon={
            <FontAwesomeIcon
              size="xl"
              icon={faUserCircle}
              className={classes.avatar}
            />
          }
        />
        <div>
          <Typography variant="h3">{`${user.FirstName} ${user.LastName}`}</Typography>
          <Typography>{user.Title}</Typography>
        </div>
      </div>
      <UserInfoField
        label="Email"
        value={user.Email}
      />
      <UserInfoField
        label="Work Phone"
        value={`${telephone.format(user.Phone)} ${user.PhoneExtension ? `Ext. ${user.PhoneExtension}` : ''}`}
      />
      <UserInfoField
        label="Cell Phone"
        value={telephone.format(user.CellPhone)}
      />
      <UserInfoField
        label="LinkedIn"
        value={user.LinkedInURL}
      />
    </>
  )
}

const UserInfoField = ({ label, value }: { label: string; value?: string }) => {
  const classes = useStyles()
  return (
    <Box className={classes.infoField}>
      <Typography>
        <b>{label}</b>
      </Typography>
      <Typography color="textSecondary">{value?.trim().length ? value : 'Not provided'}</Typography>
    </Box>
  )
}

const EditProfile = ({
  user,
  setIsEditing,
}: {
  user: User
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const classes = useStyles()
  const dispatch = useAppDispatch()

  /** Avatar upload state and handlers */
  const [showDelete, setShowDelete] = useState(false)
  const setAvatarError = useSnackbar('An error occurred while deleting the avatar.')

  const onPickImage = useCallback(
    (image: PickerFileMetadata) => {
      dispatch(userActions.updateAvatar(image.key))
    },
    [dispatch],
  )

  const onImageUpload = useImageUpload({
    onPickImage,
  })

  // const updateAvatar = (key: string) => {
  //   dispatch(userActions.updateAvatar(key))
  // }
  const deleteAvatar = () => {
    api
      .delete('/api/user/avatar')
      .then(() => {
        setShowDelete(false)
        return dispatch(userActions.fetchUser())
      })
      .catch(() => {
        setAvatarError()
        setShowDelete(false)
      })
  }

  /** User information state and handlers */
  const [editUser, setEditUser] = useState(user)
  const [errors, setErrors] = useState({
    Phone: false,
    PhoneExtension: false,
    CellPhone: false,
    LinkedInURL: false,
  })
  const [isSaving, setIsSaving] = useState(false)

  const setProfileError = useSnackbar('An error occurred while saving your profile.')
  const onSave = () => {
    setIsSaving(true)
    dispatch(userActions.updateUser(editUser))
      .then(() => {
        setIsEditing(false)
      })
      .catch((e) => {
        console.error('Error updating user profile', e)
        setProfileError()
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  const userChanged = () => {
    return (
      editUser.FirstName !== user.FirstName ||
      editUser.MiddleName !== user.MiddleName ||
      editUser.LastName !== user.LastName ||
      editUser.Title !== user.Title ||
      editUser.Phone !== user.Phone ||
      editUser.PhoneExtension !== user.PhoneExtension ||
      editUser.CellPhone !== user.CellPhone ||
      editUser.LinkedInURL !== user.LinkedInURL
    )
  }

  return (
    <>
      <div className={classes.avatarWrapper}>
        <Avatar
          aria-label="user icon"
          variant="circular"
          sourceUrl={imageAsFullImageSrc(user.AvatarUrl)}
          className={classes.avatar}
          fallbackIcon={
            <FontAwesomeIcon
              size="xl"
              icon={faUserCircle}
              className={classes.avatar}
            />
          }
        />
        <Button
          variant="contained"
          color="primary"
          className={classes.editAvatarButton}
          onClick={onImageUpload}
        >
          {user.AvatarUrl ? 'Edit Photo' : 'Add Photo'}
        </Button>
        {user.AvatarUrl ? (
          <Button
            variant="text"
            color="primary"
            className={classes.editAvatarButton}
            onClick={() => setShowDelete(true)}
          >
            Remove Photo
          </Button>
        ) : null}
      </div>
      <Box
        display="flex"
        gridGap={16}
      >
        <TextField
          label="First Name"
          value={editUser.FirstName}
          required
          onChange={(e) => setEditUser({ ...editUser, FirstName: e.target.value })}
          variant="outlined"
          margin="normal"
          fullWidth
        />
        <TextField
          label="Middle Name"
          value={editUser.MiddleName}
          onChange={(e) => setEditUser({ ...editUser, MiddleName: e.target.value })}
          variant="outlined"
          margin="normal"
          fullWidth
        />
        <TextField
          label="Last Name"
          value={editUser.LastName}
          required
          onChange={(e) => setEditUser({ ...editUser, LastName: e.target.value })}
          variant="outlined"
          margin="normal"
          fullWidth
        />
      </Box>
      <TextField
        label="Title"
        value={editUser.Title}
        onChange={(e) => setEditUser({ ...editUser, Title: e.target.value })}
        variant="outlined"
        margin="normal"
        fullWidth
      />
      <TextField
        label="Email"
        value={editUser.Email}
        disabled
        variant="outlined"
        margin="normal"
        fullWidth
      />
      <Box
        display="flex"
        gridGap={16}
      >
        <TextField
          label="Work Phone"
          value={telephone.format(editUser?.Phone)}
          onChange={(e) => {
            const num = e.target.value
            const fmtNum = telephone.unformat(num)
            const isPhoneValid = validatePhoneNum(fmtNum ?? '')

            setEditUser({ ...editUser, Phone: fmtNum })
            setErrors({ ...errors, Phone: !isPhoneValid })
          }}
          variant="outlined"
          margin="normal"
          fullWidth
          helperText="(###)-###-####"
          error={errors.Phone}
        />
        <TextField
          label="Ext."
          inputProps={{ maxLength: 5 }}
          value={editUser?.PhoneExtension}
          onChange={(e) => {
            const PhoneExtension = e.target.value
            const isExtValid = PhoneExtension.length === 0 || /^\d+$/.test(PhoneExtension)
            setErrors({ ...errors, PhoneExtension: !isExtValid })
            setEditUser({ ...editUser, PhoneExtension })
          }}
          variant="outlined"
          margin="normal"
          fullWidth
          error={errors.PhoneExtension}
        />
      </Box>
      <TextField
        label="Cell Phone"
        value={telephone.format(editUser?.CellPhone)}
        onChange={(e) => {
          const num = e.target.value
          const fmtNum = telephone.unformat(num)
          const isCellValid = validatePhoneNum(fmtNum ?? '')
          setErrors({ ...errors, CellPhone: !isCellValid })
          setEditUser({ ...editUser, CellPhone: fmtNum })
        }}
        variant="outlined"
        margin="normal"
        fullWidth
        helperText="(###)-###-####"
        error={errors.CellPhone}
      />
      <TextField
        label="LinkedIn Profile URL"
        value={editUser.LinkedInURL}
        onChange={(e) => {
          const url = e.target.value.trim()
          const isUrlValid =
            url.length === 0 || /^(https?:\/\/)?([a-z]{2,3}\.)?linkedin\.com\/in\/[a-zA-Z0-9_-]{3,}\/?$/i.test(url)
          setErrors({ ...errors, LinkedInURL: !isUrlValid })
          setEditUser({ ...editUser, LinkedInURL: url })
        }}
        variant="outlined"
        margin="normal"
        fullWidth
        error={errors.LinkedInURL}
      />
      <Button
        variant="contained"
        color="primary"
        onClick={onSave}
        disabled={
          !userChanged() ||
          isSaving ||
          errors.Phone ||
          errors.PhoneExtension ||
          errors.CellPhone ||
          !editUser.FirstName ||
          !editUser.LastName
        }
      >
        Save
      </Button>
      <ConfirmationDialog
        open={showDelete}
        headerText="Confirm Delete"
        onConfirm={() => deleteAvatar()}
        onClose={() => setShowDelete(false)}
      >
        <Typography>Are you sure you want to remove this image from your profile?</Typography>
      </ConfirmationDialog>
    </>
  )
}

export default ProfileContent
